/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { Asset, Ducks } from '@wit/vtv-x-foundation-lib';
import * as Gdpr from 'core/store/ducks/gdpr';
import { useHistoryWithState } from 'hooks/useHistoryWithState';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PrivateRoutePath } from 'routes/paths';
import translate from 'services/i18n/translate';
import RouterUtils from 'services/utils/RouterUtils';

import * as OnDemand from 'store/ducks/onDemand';
import { RailBackgroundImageName } from 'types/components/ColdStart';
import { RailType } from 'types/Rail';

import { Hero, Rail, Spinner } from 'ui/components';
import ColdStartRail from 'ui/components/Rail/ColdStartRail';
import OptInRail from 'ui/components/recommendations/OptInRail';
import { useRouter } from '../../../hooks/useRouter';

export default function MainPage(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistoryWithState();
  const router = useRouter();

  const [isWaitingGdprConfirmation, setIsWaitingGdprConfirmation] = useState(false);

  const rails = useSelector(OnDemand.Selectors.getRails);
  const sectionAsset = useSelector(OnDemand.Selectors.getSectionAsset);
  const isProcessed = useSelector(OnDemand.Selectors.isProcessed);
  const isRecommEnable = useSelector(Ducks.Recommendations.Selectors.isFeatureEnabled);
  const isGdprConsent = useSelector(Gdpr.Selectors.hasAgreedToConsent);

  useEffect(() => {
    dispatch(OnDemand.ActionCreators.init());
  }, [dispatch]);

  if (!isProcessed) {
    return (
      <div className="layout-spinner-container">
        <Spinner isActive />
      </div>
    );
  }

  const onCardClick = (asset: Asset, menuItemId?: number) => {
    router.push({ asset, menuItemId });
  };

  const onHeaderClick = (id: number | string) => {
    const rail = rails.find((el) => el.id === id);
    if (rail?.withCategories) {
      history.push(RouterUtils.getRouteWithDynamicValues(PrivateRoutePath.ONDEMAND_NEXT_LEVEL, [rail.id]));
    } else if (rail?.hasMore) {
      history.push(RouterUtils.getRouteWithDynamicValues(PrivateRoutePath.ONDEMAND_GRID, [rail.id]));
    }
  };

  return (
    <div id="OnDemand_mainPage" className="ondemand content content-fadein">
      <div className="hero margin-bottom-28">
        <Hero
          title={sectionAsset?.title}
          backgroundImage={sectionAsset?.backdrop?.uri || ''}
          onClick={() => sectionAsset && onCardClick(sectionAsset, rails.length ? rails[0].id : undefined)}
        />
      </div>

      <div className="content-container">
        {rails.map((rail) => {
          if (
            isRecommEnable &&
            rail.type === RailType.RECOMMENDATIONS &&
            (!isGdprConsent || (isGdprConsent && isWaitingGdprConfirmation))
          ) {
            return (
              <div className="rail-component-wrapper" key={rail.id}>
                <OptInRail
                  railId={rail.id}
                  railName={rail.title}
                  railBackgroundImageName={RailBackgroundImageName.RELATED_RECOMM}
                  railDescription={translate['firstTimeUse.recommendations.optIn.empty']}
                  optInClicked
                  continueClicked={() => setIsWaitingGdprConfirmation(false)}
                  onOptInClick={() => setIsWaitingGdprConfirmation(true)}
                />
              </div>
            );
          }

          if (isRecommEnable && rail.type === RailType.RECOMMENDATIONS && (!rail.assets.length || rail.loadingAssets)) {
            return (
              <div className="rail-component-wrapper" key={rail.id}>
                <ColdStartRail
                  railId={rail.id}
                  className="no-background"
                  railName={rail.title}
                  railBackgroundImageName={RailBackgroundImageName.RELATED_RECOMM}
                  railDescription={translate['firstTimeUse.recommendations.optIn.empty']}
                  requiresPrivacyConsent={!isGdprConsent}
                />
              </div>
            );
          }

          if (!rail.loadingAssets && !rail.assets.length) {
            return null;
          }

          return (
            <div className="rail-component-wrapper" key={rail.id}>
              <Rail
                className="no-background"
                railId={rail.id}
                menuItemId={rail.id}
                railName={rail.title}
                channelId={rail.channelId}
                assets={rail.assets}
                hasMore={rail.hasMore}
                isLoadingAssets={rail.loadingAssets}
                onCardClick={onCardClick}
                onHeaderClick={onHeaderClick}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
